import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import StatusBadge from "@/components/common/StatusBadge";
import { publicApi } from "@/lib/api";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function BeyondScienceSeries() {
  const [books, setBooks] = useState([]);
  useEffect(() => {
    publicApi.books().then(setBooks).catch(() => {});
  }, []);

  return (
    <PublicLayout title="Beyond Science Series">
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-36 md:pt-44 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 bsca-fade-up">
          <SectionEyebrow dark>The Beyond Science Book Series</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
            Five volumes. <span className="text-[#D4AF37]">One arc of inquiry.</span>
          </h1>
          <p className="mt-7 text-white/75 text-lg max-w-2xl">
            From cosmology to consciousness to the integration of science and spirit — a public, rigorous map of the deepest questions.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 space-y-20">
          {books.map((b, i) => (
            <article
              key={b.slug}
              data-testid={`book-row-${b.slug}`}
              className={`grid md:grid-cols-12 gap-10 md:gap-14 items-center ${
                i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="md:col-span-5">
                <Link to={`/beyond-science/${b.slug}`} className="block bsca-lift">
                  <div className="aspect-[3/4] relative bg-[#0A1120] overflow-hidden shadow-2xl">
                    <img src={b.cover_image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <StatusBadge status={b.status} label={b.status_label} />
                    </div>
                  </div>
                </Link>
              </div>
              <div className="md:col-span-7">
                <div className="bsca-eyebrow text-[#7A5A06]">Volume {b.volume} · {b.series_title || "Beyond Science"}</div>
                <h2 className="mt-3 font-serif text-3xl md:text-5xl tracking-tight leading-tight">{b.title}</h2>
                <div className="mt-2 text-[#4B5563] italic">{b.subtitle}</div>
                <div className="mt-6 border-l-2 border-[#D4AF37] pl-5">
                  <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Core Question</div>
                  <p className="mt-1 font-serif text-xl">{b.core_question}</p>
                </div>
                <p className="mt-6 text-[#1f2937] leading-relaxed">{b.description}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={`/beyond-science/${b.slug}`} data-testid={`btn-explore-${b.slug}`} className="bsca-lift inline-flex items-center bg-[#050B14] text-white font-medium px-6 py-3 rounded-sm">
                    Explore Book <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                  <Link to="/engage#join" data-testid={`btn-join-from-${b.slug}`} className="bsca-lift inline-flex items-center border border-[#050B14] text-[#050B14] px-6 py-3 rounded-sm hover:bg-[#050B14] hover:text-white">
                    Join the Alliance
                  </Link>
                </div>
                {b.retail_links && Object.values(b.retail_links).some(Boolean) && (
                  <div className="mt-7">
                    <div className="bsca-eyebrow text-[#7A5A06] text-[10px] mb-3">Buy</div>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(b.retail_links).map(([k, v]) =>
                        v ? (
                          <a
                            key={k}
                            href={v}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`retail-${b.slug}-${k}`}
                            className="text-sm bsca-link inline-flex items-center text-[#050B14]"
                          >
                            {labelForLink(k)} <ArrowUpRight className="h-3 w-3 ml-1" />
                          </a>
                        ) : null
                      )}
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bsca-dark bsca-cosmic bsca-grain py-24 relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionEyebrow dark>Beyond reading</SectionEyebrow>
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">From reader to fellow.</h2>
          <p className="mt-5 text-white/70">Membership is free. Bring your questions to the alliance.</p>
          <Link to="/engage#join" data-testid="btn-series-cta" className="mt-10 inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-8 py-4 rounded-sm bsca-lift">
            Join BSCA <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}

function labelForLink(key) {
  const map = {
    amazon_us: "Amazon US",
    amazon_india: "Amazon India",
    inkscribe: "InkScribe Store",
    flipkart: "Flipkart",
    kindle: "Kindle",
    google_books: "Google Books",
    amazon_prime: "Amazon Prime",
  };
  return map[key] || key;
}
